import React from 'react';
import { api } from '../lib/api';
import { palette as T, type } from '../design';

const FONT = "'Plus Jakarta Sans', sans-serif";
const HEADING = { fontFamily: "'Lora', serif", fontWeight: 700, fontSize: 24, margin: '0 0 12px' };
const BODY = { fontFamily: FONT, fontSize: 15, color: T.walnut, lineHeight: 1.6, margin: '0 0 24px' };
const PRIMARY = {
  background: T.terracotta, color: T.white, border: 'none', borderRadius: 12, padding: '12px 24px',
  fontFamily: FONT, fontWeight: 600, fontSize: type.body, cursor: 'pointer',
};

// The stop link is hash-routed (#/stop-emails?token=...), so the token is in the hash, never
// in a URL the browser sends to a server.
function tokenFromHash(hash) {
  const q = hash.indexOf('?');
  return q !== -1 ? new URLSearchParams(hash.slice(q + 1)).get('token') : null;
}

// Where the stop link from a "Notify me" email lands. Nothing stops on load: email scanners open
// links, so the follow ends only when the student presses Stop emails. No sign-in needed.
export function StopEmailsPage({ setPage }) {
  const [token] = React.useState(() => tokenFromHash(window.location.hash));
  const [status, setStatus] = React.useState(token ? 'ready' : 'error');
  const [message, setMessage] = React.useState(
    token ? '' : 'This link is incomplete. Open the link in your email again.',
  );
  const [instructorName, setInstructorName] = React.useState('');

  const stop = async () => {
    setMessage('');
    setStatus('stopping');
    try {
      const data = await api.post('/follows/stop', { token });
      setInstructorName(data?.instructorName || '');
      setStatus('stopped');
    } catch (err) {
      if (err?.status === 404) {
        // Out of date: already used, or never valid. Pressing again cannot help.
        setMessage(err?.data?.message || 'This link is out of date.');
        setStatus('error');
        return;
      }
      // Anything else (offline, too many tries) can work on a second press.
      setMessage('We could not stop these emails just now. Wait a minute, then press Stop emails again.');
      setStatus('ready');
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px 40px', background: T.ivory }}>
      <div data-testid="stop-emails" style={{ width: '100%', maxWidth: 420, background: T.white, borderRadius: 20, border: '1px solid ' + T.border, padding: '32px 28px', textAlign: 'center' }}>
        {(status === 'ready' || status === 'stopping') && (
          <>
            <h1 style={{ ...HEADING, color: T.espresso }}>Stop emails about new sessions?</h1>
            <p style={BODY}>
              You chose &quot;Notify me&quot; on an instructor&apos;s profile. Press Stop emails and we will no
              longer email you when that instructor publishes a session.
            </p>
            <button onClick={stop} disabled={status === 'stopping'} style={{ ...PRIMARY, opacity: status === 'stopping' ? 0.6 : 1 }}>
              {status === 'stopping' ? 'Stopping…' : 'Stop emails'}
            </button>
            {message && (
              <p role="alert" style={{ fontFamily: FONT, fontSize: type.body, color: T.errorRed, margin: '16px 0 0' }}>{message}</p>
            )}
          </>
        )}
        {status === 'stopped' && (
          <>
            <h1 style={{ ...HEADING, color: T.sageGreen }}>Emails stopped</h1>
            <p style={BODY}>
              You will no longer get an email when {instructorName || 'this instructor'} publishes a session.
              To hear again, tap &quot;Notify me&quot; on their profile.
            </p>
            <button onClick={() => setPage('marketplace')} style={PRIMARY}>Browse instructors</button>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 style={{ ...HEADING, color: T.errorRed }}>We could not stop these emails</h1>
            <p style={{ ...BODY, marginBottom: 8 }}>{message}</p>
            <p style={BODY}>
              If you already stopped them, you have nothing more to do. Otherwise, open the instructor&apos;s
              profile and tap &quot;Stop notifying me&quot;.
            </p>
            <button onClick={() => setPage('marketplace')} style={PRIMARY}>Browse instructors</button>
          </>
        )}
      </div>
    </div>
  );
}
